import { FunctionComponent, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    TextField,
} from "@mui/material";
import { DigitalCardChangeCategory } from "../../../constants/DigitalCardChangeCategory";

const DigitalCardRequestChangesModal: FunctionComponent<{
    isOpen: boolean;
    designLabel: string | null;
    // Template designs are Skip's own asset, never user content — changes
    // requested on one are always about the intro message, so there's
    // nothing for the admin to choose. Only Custom (uploaded) cards get
    // the image/message checkboxes below.
    isTemplate: boolean;
    onClose: () => void;
    onSubmit: (reason: string, categories: DigitalCardChangeCategory[]) => void | Promise<void>;
}> = ({ isOpen, designLabel, isTemplate, onClose, onSubmit }) => {
    const [reason, setReason] = useState('');
    const [needsImage, setNeedsImage] = useState(false);
    const [needsMessage, setNeedsMessage] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const hasReason = reason.trim().length > 0;
    const hasCategory = isTemplate || needsImage || needsMessage;
    const canSubmit = hasReason && hasCategory;

    function resetState() {
        setReason('');
        setNeedsImage(false);
        setNeedsMessage(false);
    }

    function handleClose() {
        resetState();
        onClose();
    }

    async function handleSubmit() {
        if (!canSubmit) return;

        const categories = isTemplate
            ? [DigitalCardChangeCategory.introMessage]
            : [
                ...(needsImage ? [DigitalCardChangeCategory.photos] : []),
                ...(needsMessage ? [DigitalCardChangeCategory.introMessage] : []),
            ];

        setSubmitting(true);
        try {
            await onSubmit(reason.trim(), categories);
            resetState();
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            sx={{ '& .MuiDialog-paper': { width: '50%' } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Request Changes
                {designLabel && (
                    <Chip
                        label={designLabel}
                        size="small"
                        color={designLabel === 'Template design' ? 'default' : 'secondary'}
                    />
                )}
            </DialogTitle>
            <DialogContent>
                {!isTemplate && (
                    <Box sx={{ mb: 1 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={needsImage}
                                    onChange={(e) => setNeedsImage(e.target.checked)}
                                />
                            }
                            label="Uploaded image needs changes"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={needsMessage}
                                    onChange={(e) => setNeedsMessage(e.target.checked)}
                                />
                            }
                            label="Intro message needs changes"
                        />
                    </Box>
                )}
                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        required
                        label="Reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                    />
                </Box>
            </DialogContent>
            <DialogActions style={{ justifyContent: 'flex-end' }}>
                <Button onClick={handleClose} sx={{ mr: 1 }}>Cancel</Button>
                <Button
                    onClick={handleSubmit}
                    color="warning"
                    variant="contained"
                    disabled={!canSubmit || submitting}
                >
                    Request Changes
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DigitalCardRequestChangesModal;
